import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  useCoupons,
  useUpdateCoupon,
  useToggleCoupon,
  useDeleteCoupon,
  useCouponRedemptions,
} from '@/hooks/useCoupons'
import { DataTable } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Coupon, CouponRedemption, UpdateCouponRequest, CouponType, CouponAppliesTo } from '@/types'
import { formatCurrency, formatDate, getApiErrorMessage } from '@/lib/utils'
import { Plus, Trash2, Eye, Pencil } from 'lucide-react'
import { toast } from 'sonner'

function RedemptionsDialog({
  couponId,
  open,
  onClose,
}: {
  couponId: string
  open: boolean
  onClose: () => void
}) {
  const { data: redemptions, isLoading } = useCouponRedemptions(couponId)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Redenciones</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : !redemptions?.length ? (
          <p className="text-sm text-muted-foreground">Sin redenciones</p>
        ) : (
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Original</TableHead>
                  <TableHead>Descuento</TableHead>
                  <TableHead>Final</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redemptions.map((r: CouponRedemption) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{r.user_id}</TableCell>
                    <TableCell>{formatCurrency(r.original_amount)}</TableCell>
                    <TableCell className="text-destructive">
                      -{formatCurrency(r.discount_amount)}
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(r.final_amount)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(r.redeemed_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function EditCouponDialog({
  coupon,
  open,
  onClose,
}: {
  coupon: Coupon
  open: boolean
  onClose: () => void
}) {
  const updateCoupon = useUpdateCoupon()

  const [form, setForm] = useState<UpdateCouponRequest>({
    type: coupon.type,
    value: coupon.value,
    applies_to: coupon.applies_to,
    max_uses: coupon.max_uses ?? undefined,
    max_uses_per_user: coupon.max_uses_per_user ?? undefined,
    min_amount: coupon.min_amount,
    max_discount: coupon.max_discount ?? undefined,
    starts_at: coupon.starts_at ? coupon.starts_at.split('T')[0] : '',
    expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : undefined,
    notes: coupon.notes,
  })

  const handleSave = async () => {
    if (form.expires_at && form.starts_at && form.starts_at > form.expires_at) {
      toast.error('La fecha de inicio no puede ser posterior a la fecha de expiracion')
      return
    }
    try {
      const updates: UpdateCouponRequest = {
        ...form,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : undefined,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      }
      await updateCoupon.mutateAsync({ id: coupon.id, updates })
      onClose()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar cupon'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar cupon: {coupon.code}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Tipo</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v as CouponType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Porcentaje</SelectItem>
                  <SelectItem value="fixed">Monto fijo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Valor {form.type === 'percentage' ? '(%)' : '(USD)'}</Label>
              <Input
                type="number"
                min={0}
                step={form.type === 'percentage' ? 1 : 0.01}
                max={form.type === 'percentage' ? 100 : undefined}
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: Number(e.target.value) }))}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Aplica a</Label>
            <Select
              value={form.applies_to}
              onValueChange={(v) => setForm((f) => ({ ...f, applies_to: v as CouponAppliesTo }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="subscription">Suscripciones</SelectItem>
                <SelectItem value="documents">Documentos</SelectItem>
                <SelectItem value="both">Ambos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Max usos totales</Label>
              <Input
                type="number"
                min={0}
                placeholder="Sin limite"
                value={form.max_uses ?? ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    max_uses: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Max usos por usuario</Label>
              <Input
                type="number"
                min={0}
                placeholder="Sin limite"
                value={form.max_uses_per_user ?? ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    max_uses_per_user: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Monto minimo (USD)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.min_amount}
                onChange={(e) => setForm((f) => ({ ...f, min_amount: Number(e.target.value) }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descuento max (USD)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                placeholder="Sin limite"
                value={form.max_discount ?? ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    max_discount: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Inicio</Label>
              <Input
                type="date"
                value={form.starts_at}
                onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Expiracion (opcional)</Label>
              <Input
                type="date"
                value={form.expires_at ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, expires_at: e.target.value || undefined }))
                }
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Notas</Label>
            <Input
              placeholder="Notas internas..."
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              maxLength={500}
            />
          </div>

          <div className="rounded-lg border bg-muted/50 p-3">
            <p className="text-xs text-muted-foreground">
              Usos actuales: {coupon.current_uses} | Creado: {formatDate(coupon.created_at)}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updateCoupon.isPending}>
            {updateCoupon.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const CouponsListPage = () => {
  const { data: coupons, isLoading } = useCoupons()
  const toggleCoupon = useToggleCoupon()
  const deleteCoupon = useDeleteCoupon()

  const [deleteTarget, setDeleteTarget] = useState<Coupon | null>(null)
  const [editTarget, setEditTarget] = useState<Coupon | null>(null)
  const [redemptionsCouponId, setRedemptionsCouponId] = useState<string | null>(null)

  const columns = [
    {
      key: 'code',
      header: 'Codigo',
      cell: (c: Coupon) => <span className="font-mono font-bold">{c.code}</span>,
    },
    {
      key: 'type',
      header: 'Tipo',
      cell: (c: Coupon) => <StatusBadge status={c.type === 'percentage' ? 'active' : 'pending'} />,
    },
    {
      key: 'value',
      header: 'Valor',
      cell: (c: Coupon) => (
        <span className="font-medium">
          {c.type === 'percentage' ? `${c.value}%` : formatCurrency(c.value)}
        </span>
      ),
    },
    {
      key: 'applies_to',
      header: 'Aplica a',
      cell: (c: Coupon) => <span className="text-sm capitalize">{c.applies_to}</span>,
    },
    {
      key: 'uses',
      header: 'Usos',
      cell: (c: Coupon) => (
        <span className="text-sm">
          {c.current_uses}/{c.max_uses ?? '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (c: Coupon) => <StatusBadge status={c.is_active ? 'active' : 'inactive'} />,
    },
    {
      key: 'expires_at',
      header: 'Expira',
      cell: (c: Coupon) => (
        <span className="text-sm text-muted-foreground">
          {c.expires_at ? formatDate(c.expires_at) : 'Sin exp.'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (c: Coupon) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setRedemptionsCouponId(c.id)}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditTarget(c)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Switch checked={c.is_active} onCheckedChange={() => toggleCoupon.mutate(c.id)} />
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(c)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Cupones</h1>
        <Button asChild>
          <Link to="/coupons/create">
            <Plus className="mr-2 h-4 w-4" />
            Crear cupon
          </Link>
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={coupons ?? []}
        isLoading={isLoading}
        keyExtractor={(c) => c.id}
        emptyMessage="No hay cupones"
      />

      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title="Eliminar cupon"
          description={`¿Eliminar el cupon ${deleteTarget.code}? Esta accion no se puede deshacer.`}
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => deleteCoupon.mutateAsync(deleteTarget.id)}
        />
      )}

      {editTarget && (
        <EditCouponDialog
          coupon={editTarget}
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}

      {redemptionsCouponId && (
        <RedemptionsDialog
          couponId={redemptionsCouponId}
          open={!!redemptionsCouponId}
          onClose={() => setRedemptionsCouponId(null)}
        />
      )}
    </div>
  )
}
